import React, { useMemo } from 'react';

/**
 * Sliding filter panel. When open this component covers the
 * viewport with a semi-transparent overlay and reveals a panel
 * anchored to the left hand side. The panel contains a series
 * of filter controls that mirror those in the provided wireframe.
 *
 * Props:
 *  - isOpen: boolean controlling visibility
 *  - onClose: function() called when the overlay is clicked
 *  - onApplyFilters: function(criteria) called when Apply is clicked
 *  - invoices: array of invoice objects to extract filter options from
 */
export default function FilterPanel({ isOpen, onClose, onApplyFilters, invoices = [] }) {
  // Local state for each filter field. These are tracked so that
  // we can return the selected values when the user clicks Apply.
  const [vendor, setVendor] = React.useState('');
  const [minAmount, setMinAmount] = React.useState('');
  const [maxAmount, setMaxAmount] = React.useState('');
  const [office, setOffice] = React.useState('');
  const [dueWithin, setDueWithin] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [ach, setAch] = React.useState('');
  const [hasAttachment, setHasAttachment] = React.useState('');

  // Dynamically extract unique vendors, QBO classes, and categories from invoices
  const { vendors, qboClasses, categories } = useMemo(() => {
    const vendorSet = new Set();
    const classSet = new Set();
    const categorySet = new Set();

    invoices.forEach((inv) => {
      if (inv.vendor) vendorSet.add(inv.vendor);
      // Extract QBO class names from the locations array (e.g., "General-Salem")
      if (Array.isArray(inv.locations)) {
        inv.locations.forEach((loc) => {
          if (loc) classSet.add(loc);
        });
      }
      if (inv.category) categorySet.add(inv.category);
    });

    return {
      vendors: Array.from(vendorSet).sort(),
      qboClasses: Array.from(classSet).sort(),
      categories: Array.from(categorySet).sort(),
    };
  }, [invoices]);

  // Only render when open. We use inline styles for the overlay and
  // panel so that the presentation is independent of any CSS build.
  if (!isOpen) return null;
  // Base styles
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 40,
    display: 'flex',
  };
  const dimStyle = {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
  };
  const panelStyle = {
    width: '320px',
    maxWidth: '100%',
    backgroundColor: '#ffffff',
    borderLeft: '1px solid #357ab2',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    overflowY: 'auto',
    padding: '16px',
  };
  const headerStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#357ab2',
    marginBottom: '16px',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#4a5568',
    marginBottom: '4px',
  };
  const inputStyle = {
    width: '100%',
    border: '1px solid #cbd5e0',
    borderRadius: '12px',
    padding: '8px',
    fontSize: '14px',
    marginBottom: '12px',
  };
  return (
    <div style={overlayStyle} aria-modal="true" role="dialog">
      {/* Overlay to dim the rest of the interface */}
      <div style={dimStyle} onClick={onClose} />
      {/* Actual filter panel */}
      <aside style={panelStyle}>
        <h2 style={headerStyle}>Filters</h2>
        {/* Vendor */}
        <div>
          <label style={labelStyle}>Vendor</label>
          <select
            style={inputStyle}
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
          >
            <option value="">Choose options</option>
            {vendors.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
        {/* Min Amount */}
        <div>
          <label style={labelStyle}>Min Amount</label>
          <input
            type="number"
            placeholder="$"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            style={inputStyle}
          />
        </div>
        {/* Max Amount */}
        <div>
          <label style={labelStyle}>Max Amount</label>
          <input
            type="number"
            placeholder="$"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            style={inputStyle}
          />
        </div>
        {/* Class (QBO Classes like General-Salem, General-Milwaukie, etc.) */}
        <div>
          <label style={labelStyle}>Class</label>
          <select
            style={inputStyle}
            value={office}
            onChange={(e) => setOffice(e.target.value)}
          >
            <option value="">Choose options</option>
            {qboClasses.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        {/* Due Within */}
        <div>
          <label style={labelStyle}>Due Within</label>
          <select
            style={inputStyle}
            value={dueWithin}
            onChange={(e) => setDueWithin(e.target.value)}
          >
            <option value="">Choose options</option>
            <option value="7">7 Days</option>
            <option value="10">10 Days</option>
            <option value="14">14 Days</option>
          </select>
        </div>
        {/* Category */}
        <div>
          <label style={labelStyle}>Category</label>
          <select
            style={inputStyle}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Choose options</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Vendor ACH Status */}
        <div>
          <label style={labelStyle}>Vendor ACH Status</label>
          <select
            style={inputStyle}
            value={ach}
            onChange={(e) => setAch(e.target.value)}
          >
            <option value="">Choose options</option>
            <option value="missing">Missing</option>
            <option value="pending">Pending</option>
            <option value="complete">Complete</option>
          </select>
        </div>

        {/* PDF Attachment */}
        <div>
          <label style={labelStyle}>PDF Attachment</label>
          <select
            style={inputStyle}
            value={hasAttachment}
            onChange={(e) => setHasAttachment(e.target.value)}
          >
            <option value="">Choose options</option>
            <option value="yes">Includes Attachment</option>
            <option value="no">No Attachment</option>
          </select>
        </div>

        {/* Apply button */}
        <div style={{ marginTop: '16px' }}>
          <button
            onClick={() => {
              // Build a filter criteria object and call onApplyFilters
              const criteria = {
                vendor,
                minAmount,
                maxAmount,
                office,
                dueWithin,
                category,
                ach,
                hasAttachment,
              };
              if (onApplyFilters) {
                onApplyFilters(criteria);
              }
            }}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#357ab2',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Apply
          </button>
        </div>
      </aside>
    </div>
  );
}